<script setup lang="ts">
  import { computed } from "vue";
  import { AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
  import { Label } from "@/components/ui/label";
  import { Select } from "@/components/episto-ui/select";
  import { Switch } from "@/components/ui/switch";
  import type { SelectOption } from "@/components/episto-ui/select";
  import ExportCustomBasesField from "./ExportCustomBasesField.vue";
  import { hasCrosstabs as formHasCrosstabs } from "@stores/survey/exports";
  import type { SurveyExportPayload } from "@/types";

  const form = defineModel<SurveyExportPayload>("form", { required: true });

  defineProps<{
    closedQuestionOptions: SelectOption[];
    baseQuestionOptions: SelectOption[];
    populationOptions: SelectOption[];
  }>();

  const hasCrosstabs = computed(() => formHasCrosstabs(form.value));
  const significanceLevelOptions = [
    { value: 0.9, label: "90%" },
    { value: 0.95, label: "95%" },
  ];
</script>

<template>
  <AccordionItem value="custom_bases">
    <AccordionTrigger class="text-base font-semibold">Custom bases</AccordionTrigger>
    <AccordionContent>
      <div class="pt-2">
        <ExportCustomBasesField
          v-model="form.custom_bases"
          :question-options="closedQuestionOptions"
          :base-options="baseQuestionOptions" />
      </div>
    </AccordionContent>
  </AccordionItem>

  <AccordionItem value="crosstabs">
    <AccordionTrigger class="text-base font-semibold">Crosstabs</AccordionTrigger>
    <AccordionContent>
      <div class="space-y-6 pt-2">
        <div class="space-y-2">
          <Label>Questions</Label>
          <Select
            v-model="form.crosstab_questions"
            :options="closedQuestionOptions"
            multiple
            filterable
            clearable
            class="w-full" />
        </div>
        <div class="space-y-2">
          <Label>Populations</Label>
          <Select
            v-model="form.crosstab_groups"
            :options="populationOptions"
            multiple
            clearable
            class="w-full" />
        </div>
        <div
          class="flex items-center justify-between gap-4"
          :class="{ 'opacity-50': !hasCrosstabs }">
          <Label class="cursor-pointer">Show group comparisons</Label>
          <Switch
            v-model="form.pairwise_comparisons"
            :disabled="!hasCrosstabs" />
        </div>
        <div
          class="space-y-2"
          :class="{ 'opacity-50': !hasCrosstabs }">
          <Label>Significance level</Label>
          <Select
            v-model="form.significance_level"
            :options="significanceLevelOptions"
            :disabled="!hasCrosstabs"
            class="w-full" />
        </div>
        <div class="flex items-center justify-between gap-4">
          <Label class="cursor-pointer">Include skipped responses</Label>
          <Switch v-model="form.include_skips" />
        </div>
      </div>
    </AccordionContent>
  </AccordionItem>
</template>
